// /crime-metrics — crimes source/sink economy dashboard (staff-only).
// Epic #550 Phase 1, issue #561.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PerCrimeRow {
  crime: number;
  day: string;
  cash_generated: number;
  xp_generated: number;
  jail_minutes: number;
  successes: number;
  jailed: number;
  attempts: number;
  success_ratio: number;
}

interface TotalsRow {
  cash_generated: number;
  xp_generated: number;
  jail_minutes: number;
  successes: number;
  jailed: number;
  attempts: number;
  success_ratio: number;
}

interface MetricsResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  per_crime: PerCrimeRow[];
  totals: TotalsRow;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format cash with ¥ symbol per D-Currency-1. */
function formatMoney(value: number): string {
  return `¥${value.toLocaleString()}`;
}

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CrimeMetricsPage() {
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<MetricsResponse>({
    queryKey: ['crime-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<MetricsResponse>(`/admin/crime-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load crime metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="Crime Metrics"
      description="Economy summary across all crime activities. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load crime metrics."
    >
      <p className="text-sm text-ink-low">
        Note: AP cost per attempt is not yet tracked (SUB-12).
      </p>

      {data != null && (
        <>
          {/* Totals */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Totals</h2>
            <div className="overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="crime-totals-table">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-ink-mid">Cash generated</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">XP generated</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Jail minutes</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Successes</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Jailed</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Attempts</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Success %</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-panel-elev">
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(data.totals.cash_generated)}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.xp_generated.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.jail_minutes.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.successes.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.jailed.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.attempts.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.success_ratio.toFixed(2)}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Per crime */}
          {data.per_crime.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-lg font-medium text-ink-high">Per crime / day</h2>
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="crime-per-crime-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Crime ID</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Cash generated</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">XP generated</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Jail minutes</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Successes</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Jailed</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Attempts</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Success %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.per_crime.map((row, idx) => (
                      <tr key={idx} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.crime}</td>
                        <td className="px-3 py-2 text-ink-low">{row.day}</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.cash_generated)}</td>
                        <td className="px-3 py-2 tabular-nums">{row.xp_generated.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.jail_minutes.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.successes.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.jailed.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.attempts.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.success_ratio.toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <p className="text-sm text-ink-low">No per-crime data available.</p>
          )}
        </>
      )}
    </EconomyDashboardLayout>
  );
}
